import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { usePracticeStatus } from '@/data/queries/usePracticeStatus';
import { Button } from '@/components/shared/ui/Button';
import { FileText, FileSpreadsheet } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { buildPlatformGetUrl, applyPlatformMedicrewWebHeaders } from '@/services/platformApiClient';

type TabType = 'curriculum' | 'evaluation' | 'practice-status';

/**
 * 밀리초를 시간 형식으로 변환 (예: 111000ms → "1분 51초")
 */
function formatPlayTime(milliseconds: number | null | undefined, t: (key: string) => string): string {
    if (milliseconds == null || milliseconds === 0) return `0${t('units.seconds')}`;
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}${t('units.hours')}`);
    if (minutes > 0) parts.push(`${minutes}${t('units.minutes')}`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}${t('units.seconds')}`);
    
    return parts.join(' ');
}

/**
 * 타임스탬프를 날짜 형식으로 변환 (예: 1721626542000 → "2026. 07. 22 16:45")
 */
function formatPracticeDate(timestamp: number | null | undefined, locale: string = 'ko'): string {
    if (timestamp == null) return '—';
    
    const date = new Date(timestamp);
    
    if (locale === 'ko') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}. ${month}. ${day} ${hours}:${minutes}`;
    }
    
    // 다른 언어는 브라우저 locale 형식 사용
    return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

export default function StudentPracticeStatus(): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const basePath = '/student';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = 'practice-status';
    
    const [showPdfHeader, setShowPdfHeader] = useState(false);
    
    // 멀티 계정 지원: currentAccount.accountId 사용
    // accountId 형식: "acc_123" → 숫자만 추출
    const userId = useMemo(() => {
        if (user?.currentAccount?.accountId) {
            const accountId = user.currentAccount.accountId;
            // "acc_123" → "123" 추출
            const match = accountId.match(/\d+/);
            const numericId = match ? match[0] : null;
            return numericId ? parseInt(numericId, 10) : user?.userId;
        }
        return user?.userId;
    }, [user]);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // PDF 다운로드 함수
    const handleDownloadPdf = async () => {
        try {
            setShowPdfHeader(true);
            await new Promise(resolve => setTimeout(resolve, 100)); // PDF 헤더 렌더링 대기
            
            const captureElement = document.querySelector('.practice-status-capture-area');
            
            if (!captureElement) {
                console.error('캡처할 영역을 찾을 수 없습니다.');
                setShowPdfHeader(false);
                return;
            }

            const canvas = await html2canvas(captureElement as HTMLElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 1440,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;

            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * contentWidth) / canvas.width;

            if (imgHeight <= pageHeight - margin * 2) {
                pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let pageCount = 0;

                while (remainingHeight > 0) {
                    if (pageCount > 0) {
                        pdf.addPage();
                    }

                    const availableHeight = pageHeight - margin * 2;
                    const sourceHeight = (availableHeight * canvas.width) / contentWidth;

                    const canvasSlice = document.createElement('canvas');
                    canvasSlice.width = canvas.width;
                    canvasSlice.height = Math.min(sourceHeight, canvas.height - sourceY);
                    const ctx = canvasSlice.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(
                            canvas,
                            0,
                            sourceY,
                            canvas.width,
                            canvasSlice.height,
                            0,
                            0,
                            canvas.width,
                            canvasSlice.height
                        );
                    }

                    const sliceData = canvasSlice.toDataURL('image/png');
                    const sliceHeight = (canvasSlice.height * contentWidth) / canvas.width;
                    pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceHeight);

                    sourceY += canvasSlice.height;
                    remainingHeight -= availableHeight;
                    pageCount++;
                }
            }

            const studentName = user?.name || '학생';
            const fileName = `${studentName}_실습현황_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);
            
            setShowPdfHeader(false);
        } catch (error) {
            console.error('PDF 생성 중 오류 발생:', error);
            setShowPdfHeader(false);
        }
    };

    // 엑셀 다운로드 함수
    const handleDownloadExcel = async () => {
        try {
            const language = i18n.language || 'ko';
            
            const queryParams: Record<string, string | number | boolean | undefined> = {
                class_id: classId,
                user_id: userId,
                language: language,
            };
            
            // URL 생성
            const url = buildPlatformGetUrl('class/lab/user/play-records/excel', { params: queryParams });
            
            // 헤더 생성
            const headers: Record<string, string> = {};
            applyPlatformMedicrewWebHeaders(headers);
            
            // Authorization 헤더 추가
            const token = apiClient.getAccessToken();
            if (token) {
                const needsEncoding = !token.includes('%');
                headers.Authorization = needsEncoding ? encodeURIComponent(token) : token;
            }
            
            // 파일 다운로드
            const response = await fetch(url, {
                method: 'GET',
                headers,
            });
            
            if (!response.ok) {
                throw new Error(`엑셀 다운로드 실패: ${response.status} ${response.statusText}`);
            }
            
            // Blob으로 변환
            const blob = await response.blob();
            
            // 다운로드 링크 생성 및 클릭
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            const studentName = user?.name || '학생';
            link.download = `${studentName}_실습현황_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            
            console.log('✅ [PracticeStatus] 엑셀 다운로드 성공');
        } catch (error) {
            console.error('❌ [PracticeStatus] 엑셀 다운로드 실패:', error);
            alert('엑셀 다운로드에 실패했습니다.');
        }
    };

    const {
        data: classDetailDto,
        isError: isClassError,
        isLoading: isClassLoading,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    // 선택된 사용자의 플레이 기록 조회
    const {
        data: practiceData,
        isError: isPracticeError,
        isLoading: isPracticeLoading,
    } = usePracticeStatus(classId, userId, {
        enabled: classId > 0 && userId != null,
    });

    // 플레이 기록 + 날짜 필터링
    const filteredRecords = useMemo(() => {
        if (!practiceData?.ScenaroPlayRecords) {
            return [];
        }
        
        return practiceData.ScenaroPlayRecords.filter((record) => {
            // 날짜 필터링
            if (startDate || endDate) {
                const recordDate = record.started_at;
                if (!recordDate) return false;
                
                const recordDateObj = new Date(recordDate);
                
                if (startDate) {
                    const startDateObj = new Date(startDate);
                    startDateObj.setHours(0, 0, 0, 0);
                    if (recordDateObj < startDateObj) return false;
                }
                
                if (endDate) {
                    const endDateObj = new Date(endDate);
                    endDateObj.setHours(23, 59, 59, 999);
                    if (recordDateObj > endDateObj) return false;
                }
            }
            
            return true;
        });
    }, [practiceData, startDate, endDate]);

    // minPassingScore 가져오기 (클래스 상세에서)
    const minPassingScore = useMemo(() => {
        return classDetailDto?.minPassingScore || 70;
    }, [classDetailDto]);

    // 상태 계산 (완료 여부)
    const getStatus = (score: number | undefined): string => {
        if (score == null) return '';
        return score >= minPassingScore ? t('practiceStatus.completed') : '';
    };

    // 실습 모드 번역
    const translateMode = (mode: string | undefined): string => {
        if (!mode) return '—';
        const lowerMode = mode.toLowerCase();
        if (lowerMode.includes('evaluat')) return t('practiceStatus.modes.evaluation');
        if (lowerMode.includes('practice')) return t('practiceStatus.modes.practice');
        if (lowerMode.includes('study')) return t('practiceStatus.modes.study');
        return mode;
    };

    const backToClassDetailPath = (): string => {
        return `${basePath}/my-classes/${classId}`;
    };

    const handleTabChange = (tab: TabType) => {
        const pathPrefix = `${basePath}/my-classes`;

        if (tab === 'curriculum') {
            // 커리큘럼 탭으로 이동
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${pathPrefix}/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            // 평가결과 탭으로 이동
            navigate(`${pathPrefix}/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            // 이미 실습현황이므로 아무것도 안 함
            return;
        }
    };

    const allTabs = [
        { id: 'curriculum', label: t('session.tabCurriculum') },
        { id: 'evaluation', label: t('session.tabEvaluation') },
        { id: 'practice-status', label: t('session.tabPracticeStatus') },
    ] as const;

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {allTabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">{t('common.dateRange')}</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder={t('common.datePlaceholder')}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <span className="text-gray-600">~</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder={t('common.datePlaceholder')}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <Button
                    onClick={handleDownloadExcel}
                    variant="outline"
                    size="sm"
                    className="ml-2"
                >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    Excel
                </Button>
                <Button
                    onClick={handleDownloadPdf}
                    variant="outline"
                    size="sm"
                >
                    <FileText className="w-4 h-4 mr-1" />
                    PDF
                </Button>
            </div>
        );
    };

    if (!classDetailDto && !isClassError) {
        return (
            <FullScreenLayout
                header={<FullScreenHeader title="…" onBack={() => navigate(backToClassDetailPath())} />}
            >
                <div className="p-8 text-gray-500">{t('myClass.loading')}</div>
            </FullScreenLayout>
        );
    }

    if (isClassError || !classItem) {
        return (
            <FullScreenLayout
                header={<FullScreenHeader title={t('session.tabPracticeStatus')} onBack={() => navigate(backToClassDetailPath())} />}
            >
                <div className="p-8 text-gray-500">{t('detail.classNotFound')}</div>
            </FullScreenLayout>
        );
    }

    const isLoading = isPracticeLoading;
    const hasError = isPracticeError;

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            {/* 메인 영역 (사이드바 없이) */}
            <div className="flex-1 bg-white overflow-y-auto">
                <div className="py-8 px-24">
                    <div className="practice-status-capture-area">
                        {/* PDF 전용 헤더 */}
                        {showPdfHeader && (
                            <div className="bg-white border-b-2 border-gray-200 pb-4 mb-6">
                                <div className="flex items-center justify-between mb-6">
                                    <img
                                        src="/assets/images/brand/medicrew_blue_logo.png"
                                        alt="Medicrew"
                                        className="h-8 object-contain"
                                    />
                                    <div className="text-xs text-gray-500">
                                        {new Date().toLocaleDateString(i18n.language)}
                                    </div>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                                    <div>
                                        <span className="font-semibold text-gray-700">클래스명:</span>
                                        <span className="ml-2 text-gray-900">{classItem?.title || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* 사용자 정보 헤더 */}
                        <div className="flex items-center gap-3 mb-6">
                            <h1 className="text-2xl font-bold text-gray-900">{user?.name}</h1>
                            <span className="text-sm text-gray-500">{user?.email}</span>
                        </div>

                    {/* 테이블 */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                        {t('practiceStatus.scenario')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                        {t('practiceStatus.practiceMode')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                        {t('practiceStatus.duration')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                        {t('practiceStatus.evaluationScore')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                        {t('practiceStatus.status')}
                                    </th>
                                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                        {t('practiceStatus.practiceDateTime')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                            {t('practiceStatus.loadingData')}
                                        </td>
                                    </tr>
                                ) : hasError ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-red-500">
                                            {t('practiceStatus.loadError')}
                                        </td>
                                    </tr>
                                ) : filteredRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                            {t('practiceStatus.noPracticeData')}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRecords.map((record, index) => (
                                        <tr
                                            key={index}
                                            className="border-b border-gray-100 hover:bg-gray-50"
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {record.content_name || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                {translateMode(record.mode_value_title || record.mode)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                {formatPlayTime(record.play_time, t)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                {record.score ?? '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-center">
                                                {getStatus(record.score) ? (
                                                    <span className="text-blue-600 font-medium">
                                                        {getStatus(record.score)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                {formatPracticeDate(record.started_at, i18n.language)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    );
}
